import { DrawerContext, DrawerPage } from '@components/drawer/useDrawer';
import FilterRoot from '@components/filter/FilterRoot';
import StyledAutocomplete from '@components/styled-autocomplete';
import useStyledAutocomplete from '@components/styled-autocomplete/useStyledAutocomplete';
import { CameraWithInfo } from '@EcamModel/model';
import { CameraNotificationStatus, CameraNotificationType } from '@EcamModel/model/CameraNotification';
import { Box, Button, Pagination as MuiPagination, Skeleton, Stack, Typography } from '@mui/material';
import { BaseSelect } from '@pages/cameras/list';
import { search } from '@pages/cameras/list/helpers';
import { BaseItem } from '@pages/cameras/list/styles';
import color from '@theme/Colors';
import { useContext, useEffect, useState } from 'react';
import BaseNotificationItem from './components/BaseNotificationItem';
import CameraNotificationItem from './components/CameraNotificationItem';
import PcnGrMatchingAlgorithmNotificationItem from './components/PcnGrMatchingAlgorithmNotificationItem';
import PcnLackVrnNotificationItem from './components/PcnLackVrnNotificationItem';
import TaskNotificationItem from './components/TaskNotificationItem';
import useNotificationContext, { NotificationContext } from './hooks/NotificationProvider';

const notificationStatus: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Read',
        Value: CameraNotificationStatus.Read,
    },
    {
        Id: 2,
        Name: 'Unread',
        Value: CameraNotificationStatus.Unread,
    },
];

const notificationTypes: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Camera Threshold',
        Value: CameraNotificationType.CameraThreshold,
    },
    {
        Id: 2,
        Name: 'Task',
        Value: CameraNotificationType.Task,
    },
    {
        Id: 3,
        Name: 'Photo Overload',
        Value: CameraNotificationType.PhotoOverload,
    },
    {
        Id: 4,
        Name: 'PC Lacking Vrn',
        Value: CameraNotificationType.PCNsLackingVrn,
    },
    {
        Id: 5,
        Name: 'PC Group Matching',
        Value: CameraNotificationType.GroupMatching,
    },
    {
        Id: 6,
        Name: 'Missing Geozone Photo',
        Value: CameraNotificationType.PTZPositionMismatch,
    },
    {
        Id: 7,
        Name: 'Camera Disconnected',
        Value: CameraNotificationType.CameraDisconnected,
    },
    {
        Id: 8,
        Name: 'Low Camera Photo',
        Value: CameraNotificationType.LowCameraPhoto,
    },
    {
        Id: 9,
        Name: 'High Camera Photo',
        Value: CameraNotificationType.HighCameraPhoto,
    },
    {
        Id: 10,
        Name: 'Low Battery',
        Value: CameraNotificationType.LowCameraBattery,
    },
];

type FilterCarParkingDetailState = {
    name: string;
    status?: BaseSelect;
    type?: BaseSelect;
    cameras?: CameraWithInfo[];
};

export default function CameraNotifications() {
    const notificationContext = useNotificationContext();
    const { setActiveItem } = useContext(DrawerContext);

    const { loading, listNotification, markAllRead, getNotifications } = notificationContext;

    const [filter, setFilter] = useState<FilterCarParkingDetailState>({
        name: '',
        status: undefined,
        cameras: undefined,
        type: undefined,
    });

    const notificationStatusAutocomplete = useStyledAutocomplete({
        list: {
            options: notificationStatus,
            isFiltered: (option, searchText) => search(option.Name, searchText),
        },
    });

    const notificationTypeAutocomplete = useStyledAutocomplete({
        list: {
            options: notificationTypes,
            isFiltered: (option, searchText) => search(option.Name, searchText),
        },
    });

    const setPartialTask = (task: Partial<FilterCarParkingDetailState>) => {
        setFilter((prev) => ({ ...prev, ...task }));
    };

    useEffect(() => {
        setActiveItem(DrawerPage.Alert);
    }, []);

    useEffect(() => {
        const statusFilter = filter.status?.Value as CameraNotificationStatus;
        const typeFilter = filter.type?.Value as CameraNotificationType;
        getNotifications(1, statusFilter, typeFilter);
    }, [filter.status, filter.type]);

    return (
        <NotificationContext.Provider value={notificationContext}>
            <Stack direction={'column'} spacing={2}>
                {/* title */}
                <Stack direction="row" justifyContent={'space-between'} alignItems={'center'}>
                    <Typography variant="h3">Notifications</Typography>
                    <Button variant="contained" onClick={markAllRead}>
                        Mark all as read
                    </Button>
                </Stack>

                {/* filter */}
                <FilterRoot
                    filterChild={
                        <Stack direction={'row'} spacing={2} width={'100%'} justifyContent={'space-between'}>
                            <Stack width={'50%'} zIndex={10}>
                                <StyledAutocomplete
                                    {...notificationStatusAutocomplete}
                                    getOptionLabel={(o) => o.Name}
                                    label="Notifications status"
                                    value={filter.status}
                                    placeholder="All notifications status"
                                    onChange={(s) => {
                                        setPartialTask({ status: s });
                                    }}
                                />
                            </Stack>
                            <Stack width={'50%'} zIndex={9}>
                                <StyledAutocomplete
                                    {...notificationTypeAutocomplete}
                                    getOptionLabel={(o) => o.Name}
                                    label="Notification type"
                                    value={filter.type}
                                    placeholder="All notification types"
                                    onChange={(t) => {
                                        setPartialTask({ type: t });
                                    }}
                                />
                            </Stack>
                        </Stack>
                    }
                    handleResetFilter={() => {
                        setPartialTask({});
                    }}
                />

                {loading ? (
                    <Stack gap={1}>
                        {[...Array(5)].map((e, i) => (
                            <SkeletonNoti key={i} />
                        ))}
                    </Stack>
                ) : listNotification?.rows?.length ? (
                    <Stack gap={1} mt={2}>
                        {listNotification.rows?.map((item, index) => {
                            switch (item.Type) {
                                case CameraNotificationType.Task:
                                    return <TaskNotificationItem key={item.Id} item={item} index={index} />;
                                case CameraNotificationType.PhotoOverload:
                                    return <BaseNotificationItem key={item.Id} item={item} index={index} />;
                                case CameraNotificationType.PCNsLackingVrn:
                                    return <PcnLackVrnNotificationItem key={item.Id} item={item} index={index} />;

                                case CameraNotificationType.GroupMatching:
                                    return (
                                        <PcnGrMatchingAlgorithmNotificationItem
                                            key={item.Id}
                                            item={item}
                                            index={index}
                                        />
                                    );

                                case CameraNotificationType.PTZPositionMismatch:
                                    return (
                                        <BaseNotificationItem
                                            key={item.Id}
                                            item={item}
                                            index={index}
                                            customNote={<Typography color="text.primary">{item.Notes}</Typography>}
                                        />
                                    );

                                case CameraNotificationType.CameraDisconnected:
                                    return <BaseNotificationItem key={item.Id} item={item} index={index} />;

                                case CameraNotificationType.LowCameraPhoto:
                                    return <BaseNotificationItem key={item.Id} item={item} index={index} />;

                                case CameraNotificationType.HighCameraPhoto:
                                    return <BaseNotificationItem key={item.Id} item={item} index={index} />;

                                case CameraNotificationType.LowCameraBattery:
                                    return <BaseNotificationItem key={item.Id} item={item} index={index} />;

                                default:
                                    return <CameraNotificationItem key={item.Id} item={item} index={index} />;
                            }
                        })}

                        {/* paging */}
                        {listNotification?.total! > listNotification?.pageSize! && !loading && (
                            <Stack mt={3} direction="row" justifyContent="center">
                                <MuiPagination
                                    count={listNotification?.totalPages || 1}
                                    page={listNotification?.page}
                                    onChange={(_, page) => {
                                        const statusFilter = filter.status?.Value as CameraNotificationStatus;
                                        const typeFilter = filter.type?.Value as CameraNotificationType;
                                        getNotifications(page, statusFilter, typeFilter);
                                    }}
                                    variant="outlined"
                                    shape="rounded"
                                />
                            </Stack>
                        )}
                    </Stack>
                ) : (
                    <Stack alignItems={'center'} px={2} height={'100px'} justifyContent={'center'}>
                        <Typography fontWeight={500}>Currently, nothing to notification!</Typography>
                        <Typography textAlign={'center'} color={color.grey600}>
                            This area will light up with new notifications once there's threshold breaches in your
                            camera overview page.
                        </Typography>
                    </Stack>
                )}
            </Stack>
        </NotificationContext.Provider>
    );
}

const SkeletonNoti = () => {
    return (
        <BaseItem
            component={Stack}
            sx={{
                display: 'flex',
                cursor: 'pointer',
            }}
            gap={2}
        >
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                <Stack direction={'row'} alignItems={'center'} gap={1} width={'100%'}>
                    <Skeleton variant="circular" animation="wave" width={10} height={10} />

                    <Stack width={'100%'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Stack direction={'row'} spacing={1} alignItems={'center'}>
                            <Skeleton variant="text" animation="wave" width={120} height={20} />
                            <Skeleton
                                variant="rectangular"
                                animation="wave"
                                width={80}
                                height={20}
                                sx={{ borderRadius: 2 }}
                            />
                        </Stack>
                        <Skeleton variant="text" animation="wave" width={60} height={16} />
                    </Stack>
                </Stack>
            </Stack>

            <Box>
                <Skeleton variant="text" animation="wave" width={100} height={12} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" animation="wave" width={300} height={16} />
                <Skeleton variant="text" animation="wave" width={250} height={16} />
            </Box>
        </BaseItem>
    );
};
